import { apiRequest, apiRequestPaginated } from './client';
import type { Complaint } from '../types';

type ApiPerson = { name?: string | null; email?: string; profilePicture?: string } | null;

type ApiReport = {
  _id: string;
  type?: 'user' | 'provider' | 'job' | string;
  reason?: string;
  description?: string | null;
  status?: string;
  createdAt?: string;
  job?: { _id?: string; title?: string; description?: string } | null;
  user?: ApiPerson;
  provider?: ApiPerson;
  reportedByUser?: ApiPerson;
  reportedByProvider?: ApiPerson;
};

const mapReportStatus = (status?: string): Complaint['status'] => {
  switch ((status || '').toLowerCase()) {
    case 'resolved':
      return 'Resolved';
    case 'pending':
    default:
      return 'Open';
  }
};

export const mapApiReport = (report: ApiReport): Complaint => {
  const submittedByUser = report.reportedByUser?.name;
  const submittedByProvider = report.reportedByProvider?.name;
  const submittedBy = submittedByUser || submittedByProvider || '—';
  const submittedByType = submittedByUser ? 'Customer' : 'Provider';

  let against = '—';
  let againstType: Complaint['againstType'] = 'Provider';
  if (report.type === 'user') {
    against = report.user?.name || 'User';
    againstType = 'Customer';
  } else if (report.type === 'provider') {
    against = report.provider?.name || 'Provider';
    againstType = 'Provider';
  } else {
    against = report.job?.title || report.provider?.name || 'Job';
    againstType = 'Provider';
  }

  return {
    id: report._id,
    jobId: report.job?._id || '—',
    submittedBy,
    submittedByType,
    against,
    againstType,
    type: report.reason || report.type || 'Report',
    description: report.description || report.job?.description || '',
    createdAt: report.createdAt || '',
    status: mapReportStatus(report.status),
    notes: [],
    history: [],
  };
};

export const reportsApi = {
  getReports: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<{ reports: Complaint[]; total: number }> => {
    const query = new URLSearchParams({
      page: String(params?.page || 1),
      limit: String(params?.limit || 1000),
    });

    const { data, pagination } = await apiRequestPaginated<{ reports: ApiReport[] }>(
      `/admin/report/get-reports?${query.toString()}`,
      { method: 'GET', auth: true }
    );

    return {
      reports: (data.reports || []).map(mapApiReport),
      total: pagination.totalItems || 0,
    };
  },

  getReportDetails: async (reportID: string): Promise<Complaint> => {
    // Backend stub currently; fall back to list lookup.
    try {
      const data = await apiRequest<{ report?: ApiReport } | ApiReport>(
        `/admin/report/get-report-details/${reportID}`,
        { method: 'GET', auth: true }
      );
      const report = (data as { report?: ApiReport }).report || (data as ApiReport);
      if (report && (report as ApiReport)._id) return mapApiReport(report as ApiReport);
    } catch {
      // continue to list fallback
    }

    const { reports } = await reportsApi.getReports({ page: 1, limit: 1000 });
    const found = reports.find((r) => r.id === reportID);
    if (!found) throw new Error('Report not found');
    return found;
  },
};
